function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        // Dawn default closes the media modal on any mouse click that isn't on deferred media.
        // With image sliders (e.g. Swiper in the product media modal) that makes it impossible to drag/slide.
        // Only close when clicking the backdrop itself.
        if (event.pointerType === 'mouse' && event.target === this) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  // connectedCallback() {
  //   this.addEventListener('change', (event) => {
  //     const target = this.getInputForEventTarget(event.target);
  //     this.updateSelectionMetadata(event);

  //     publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
  //       data: {
  //         event,
  //         target,
  //         selectedOptionValues: this.selectedOptionValues,
  //       },
  //     });
  //   });

  //   this.addEventListener('input', (event) => {
  //     if (event.target.tagName !== 'SELECT') return;
    
  //     const target = this.getInputForEventTarget(event.target);
  //     this.updateSelectionMetadata(event);
    
  //     publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
  //       data: {
  //         event,
  //         target,
  //         selectedOptionValues: this.selectedOptionValues,
  //       },
  //     });
  //   });
  // }

  connectedCallback() {
    // Debounce collapses rapid changes into a single fetch (prevents 429s)
    this._publishOptionChange =
      this._publishOptionChange ||
      debounce((event) => {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);
  
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      }, 150);
  
    this.addEventListener('change', (event) => {
      this._publishOptionChange(event);
    });
  }
  
  
  

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const prev = Array.from(target.options).find((o) => o.hasAttribute('selected'));
      prev?.removeAttribute('selected');
      target.selectedOptions[0]?.setAttribute('selected', 'selected');


      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      // const selectedDropdownSwatchValue = target
      //   .closest('.product-form__input')
      //   .querySelector('[data-selected-value] > .swatch');
      // if (!selectedDropdownSwatchValue) return;
      const wrapper = target.closest('.product-form__input');
      const selectedDropdownSwatchValue = wrapper?.querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;

      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    const values = [];
  
    // dropdowns
    this.querySelectorAll('select').forEach((select) => {
      const opt = select.selectedOptions?.[0];
      if (opt?.dataset?.optionValueId) values.push(opt.dataset.optionValueId);
    });
  
    // radios (buttons / swatches)
    this.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      if (input?.dataset?.optionValueId) values.push(input.dataset.optionValueId);
    });
  
    return values;
  }
  
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



// Custom js


(() => {
  const disableATC = () => {
    document.querySelectorAll('product-form [type="submit"]').forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('data-atc-disabled-until-productinfo', 'true');
    });
  };

  const enableATC = () => {
    document.querySelectorAll('product-form [type="submit"][data-atc-disabled-until-productinfo="true"]').forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.removeAttribute('data-atc-disabled-until-productinfo');
    });
  };

  disableATC();

  document.addEventListener('product-info:loaded', () => {
    enableATC();
  });
})();
document.addEventListener('DOMContentLoaded', function() {
  if (window.innerWidth < 749) {
  document.querySelectorAll('.product').forEach(function(product) {
    var title = product.querySelector('.product__title');
    var price = product.querySelector('.nx-from-price');
    var sale = product.querySelector('.pdp-sale-strip');

    if (price) product.insertBefore(price, product.firstChild);
    if (title) product.insertBefore(title, product.firstChild);
    if (sale) product.insertBefore(sale, title); // append ABOVE title
  });
}


  // var customSpan = document.querySelector('.custom_sze_variant');
  // var variantSelect = document.querySelector('[data-product-select]') || 
  //                     document.querySelector('select[name="id"]');

  // if (!customSpan || !variantSelect) return;

  // function updateLabel() {
  //   var selectedOption = variantSelect.options[variantSelect.selectedIndex];
  //   if (selectedOption) {
  //     customSpan.textContent = selectedOption.textContent.trim();
  //   }
  // }

  // variantSelect.addEventListener('change', updateLabel);
  // updateLabel();

});

// document.addEventListener('DOMContentLoaded', function () {
//   const customSpan = document.querySelector('.custom_sze_variant');
//   const variantSelects = document.querySelector('variant-selects');
//   if (!customSpan || !variantSelects) return;

//   const updateLabelFromSelectedSize = () => {
//     // Try to find the Size dropdown (common cases)
//     // const sizeSelect =
//     //   variantSelects.querySelector('select[name^="options"][name*="Size"]') ||
//     //   variantSelects.querySelector('select[name^="options"][name*="size"]');

//     const sizeSelect = variantSelects.querySelector('select[name="options[size]"]');


//     if (sizeSelect && sizeSelect.selectedOptions?.[0]) {
//       customSpan.textContent = sizeSelect.selectedOptions[0].textContent.trim();
//       return;
//     }

//     // Fallback: if using radio pills/swatches for size
//     const checkedRadio = variantSelects.querySelector('fieldset input[type="radio"]:checked');
//     if (checkedRadio) {
//       customSpan.textContent = checkedRadio.value.trim();
//       return;
//     }
//   };

//   // Update initially
//   updateLabelFromSelectedSize();

//   // Update whenever any option changes
//   variantSelects.addEventListener('change', updateLabelFromSelectedSize);
  

//   // Optional: also react after Dawn swaps product-info HTML (variant change)
//   document.addEventListener('variant:change', updateLabelFromSelectedSize);
// });

document.addEventListener('DOMContentLoaded', function () {
  const customSpan = document.querySelector('.custom_sze_variant');
  const variantSelects = document.querySelector('variant-selects');
  if (!customSpan || !variantSelects) return;

  const updateLabelFromSelectedSize = () => {
    const sizeSelect = variantSelects.querySelector('select[name="options[size]"]');

    if (sizeSelect?.selectedOptions?.[0]) {
      customSpan.textContent = sizeSelect.selectedOptions[0].textContent.trim();
      return;
    }

    const checkedRadio = variantSelects.querySelector('fieldset input[type="radio"]:checked');
    if (checkedRadio) {
      customSpan.textContent = checkedRadio.value.trim();
    }
  };

  updateLabelFromSelectedSize();
  variantSelects.addEventListener('change', updateLabelFromSelectedSize);

  // Note: Dawn doesn't emit "variant:change" by default. This line can stay,
  // but it likely won't fire unless you created it elsewhere.
  document.addEventListener('variant:change', updateLabelFromSelectedSize);
});



// Move quantity according to screen size
  const moveQuantity = () => {
    const qtyEl = document.querySelector('.product-form__quantity');
    const buttonsEl = document.querySelector('.product-form__buttons');
    const priceEl = document.querySelector('.price_pdp_main_wr');

    if (!qtyEl) return; // stop if quantity not found

    // Keep qty + add-to-cart together (same as desktop layout) on all breakpoints.
    if (buttonsEl && !buttonsEl.contains(qtyEl)) {
      buttonsEl.prepend(qtyEl);
    }
  };

  // Move once on page load
  moveQuantity();

  // Move again on resize
  window.addEventListener("resize", function () {
    moveQuantity();
  });


document.addEventListener("DOMContentLoaded", function () {
  const selectorList = [
    "variant-selects.wrap_in_div",
    ".product__inventory.wrap_in_div",
    ".price_pdp_main_wr.wrap_in_div",
    ".wrap_in_div.installment_div",
    ".product-form__quantity",
    ".wrap_in_div.main_buy_btn",
    ".nx-delivery-banner.wrap_in_div"
  ];

  const elements = selectorList
    .map(sel => document.querySelector(sel))
    .filter(Boolean);

  // Prevent moving nested elements out of their parent.
  // Example: on desktop, JS moves `.product-form__quantity` into `.wrap_in_div.main_buy_btn`.
  // If we then wrap both, we must not append the child separately (it would be removed from the parent).
  const topLevelElements = elements.filter((el, idx) =>
    !elements.some((other, jdx) => jdx !== idx && other.contains(el))
  );

  if (topLevelElements.length === 0) return;

  const wrapper = document.createElement("div");
  wrapper.className = "wrap_in_div_main";

  // Insert wrapper before the first element
  topLevelElements[0].before(wrapper);

  // Move all elements inside wrapper
  topLevelElements.forEach(el => wrapper.appendChild(el));

  const desktopLayoutQuery = window.matchMedia('(min-width: 1200px)');

  const moveSelectorPanel = () => {
    const productLayout = document.querySelector('.main_product .product');
    const infoContainer = document.querySelector('.main_product .product__info-container');
    const selectorPanel = document.querySelector('.main_product .wrap_in_div_main');
    if (!productLayout || !infoContainer || !selectorPanel) return;

    let selectorColumn = productLayout.querySelector('.pdp-selector-column');

    if (desktopLayoutQuery.matches) {
      if (!selectorColumn) {
        selectorColumn = document.createElement('div');
        selectorColumn.className = 'grid__item product__selector-wrapper pdp-selector-column';
        productLayout.appendChild(selectorColumn);
      }

      if (!selectorColumn.contains(selectorPanel)) {
        selectorColumn.appendChild(selectorPanel);
      }
      return;
    }

    if (selectorColumn && selectorColumn.contains(selectorPanel)) {
      infoContainer.appendChild(selectorPanel);
    }

    selectorColumn?.remove();
  };

  moveSelectorPanel();
  window.addEventListener('resize', moveSelectorPanel);
});

function syncProductExtrasVisibility() {
  const extrasRoot = document.querySelector('.pdp-extras');
  if (!extrasRoot) return;

  const persistedState = window.__pdpExtrasPersistedState || {
    handlePack: null,
    doorFrame: null,
    isVisible: false,
  };

  const shouldShow = Boolean(
    persistedState.isVisible ||
      persistedState.handlePack ||
      persistedState.doorFrame ||
      extrasRoot.dataset.handlePack ||
      extrasRoot.dataset.doorFrame
  );

  console.log('[pdp-extras]', 'sync:visibility', {
    shouldShow,
    hiddenBefore: extrasRoot.hidden,
    persistedState,
    datasetHandlePack: extrasRoot.dataset.handlePack || null,
    datasetDoorFrame: extrasRoot.dataset.doorFrame || null,
  });

  if (shouldShow) {
    extrasRoot.hidden = false;
  }
}

function moveProductExtrasIntoSelectorPanel() {
  const extrasRoot = document.querySelector('.pdp-extras');
  const selectorPanel = document.querySelector('.main_product .wrap_in_div_main');
  const variantSelects = document.querySelector('.main_product variant-selects.wrap_in_div');

  if (!extrasRoot || !selectorPanel || !variantSelects) return;

  const isInsideSelectorPanel = selectorPanel.contains(extrasRoot);

  console.log('[pdp-extras]', 'layout:check', {
    isInsideSelectorPanel,
    extrasParent: extrasRoot.parentElement?.className || extrasRoot.parentElement?.tagName || null,
    selectorPanelChildren: selectorPanel.children.length,
  });

  if (!isInsideSelectorPanel) {
    variantSelects.insertAdjacentElement('afterend', extrasRoot);
    console.log('[pdp-extras]', 'layout:moved', {
      newParent: extrasRoot.parentElement?.className || extrasRoot.parentElement?.tagName || null,
    });
  }
}

function initProductExtras() {
  const extrasRoot = document.querySelector('.pdp-extras');
  const variantSelects = document.querySelector('variant-selects');
  if (!extrasRoot || !variantSelects) return;

  const extrasDebug = (label, payload = {}) => {
    console.log('[pdp-extras]', label, payload);
  };

  extrasDebug('init:start', {
    hidden: extrasRoot.hidden,
    hasHandlePack: Boolean(extrasRoot.dataset.handlePack),
    hasDoorFrame: Boolean(extrasRoot.dataset.doorFrame),
  });

  const existingState = window.__pdpExtrasState;
  if (existingState?.cleanup) {
    extrasDebug('cleanup:previous-instance');
    existingState.cleanup();
  }

  const extrasSelectionInput = document.querySelector('[data-extras-selection]');
  const extrasPayloadInput = document.querySelector('[data-extras-payload]');
  const cleanupCallbacks = [];
  const persistedState = window.__pdpExtrasPersistedState || {
    handlePack: null,
    doorFrame: null,
    isVisible: false,
  };

  if (persistedState.handlePack) {
    extrasRoot.dataset.handlePack = persistedState.handlePack;
  }

  if (persistedState.doorFrame) {
    extrasRoot.dataset.doorFrame = persistedState.doorFrame;
  }

  // Syncs the size label span to show/hide the selected value.
  const getSizeSelect = () =>
    Array.from(variantSelects.querySelectorAll('select[name^="options["]')).find(
      (s) => (s.getAttribute('name') || '').toLowerCase().includes('size')
    );

  const syncSizeLabel = (select) => {
    if (!select) return;
    const label = variantSelects.querySelector(`label[for="${select.id}"]`);
    const span = label?.querySelector('.custom_sze_variant');
    if (!span) return;
    span.hidden = true;
  };

  // Reset size select to placeholder on first load so the user must actively choose a size.
  // Skip if the user has already interacted this session (flag persists across AJAX re-inits).
  const sizeSelectEl = getSizeSelect();
  if (!window.__pdpSizeInteracted) {
    if (sizeSelectEl) {
      if (!sizeSelectEl.querySelector('option[value=""]')) {
        const ph = document.createElement('option');
        ph.value = '';
        ph.disabled = true;
        ph.textContent = 'Choose a size';
        sizeSelectEl.insertBefore(ph, sizeSelectEl.firstChild);
      }
      sizeSelectEl.value = '';
      syncSizeLabel(sizeSelectEl);
    }
  } else {
    // AJAX re-init after user has already chosen — restore the label.
    syncSizeLabel(sizeSelectEl);
  }

  extrasDebug('state:rehydrated', {
    persistedState,
    datasetHandlePack: extrasRoot.dataset.handlePack || null,
    datasetDoorFrame: extrasRoot.dataset.doorFrame || null,
  });

  const extrasConfigs = [
    {
      key: 'handlePack',
      collectionAttr: 'data-handle-pack-collection',
      productsAttr: 'data-handle-pack-products',
      modalSelector: '[data-handle-pack-modal]',
      listSelector: '[data-handle-pack-list]',
      openSelector: '[data-open-handle-pack-modal]',
      closeSelector: '[data-close-handle-pack-modal]',
      countSelector: '[data-handle-pack-count]',
      title: 'handle packs',
    },
    {
      key: 'doorFrame',
      useVariantFrames: true,
      modalSelector: '[data-door-frame-modal]',
      listSelector: '[data-door-frame-list]',
      openSelector: '[data-open-door-frame-modal]',
      closeSelector: '[data-close-door-frame-modal]',
      countSelector: '[data-door-frame-count]',
      title: 'door frames',
    },
  ].map((config) => ({
    ...config,
    modal: extrasRoot.querySelector(config.modalSelector),
    list: extrasRoot.querySelector(config.listSelector),
    openButtons: extrasRoot.querySelectorAll(config.openSelector),
    closeButtons: extrasRoot.querySelectorAll(config.closeSelector),
    countEls: extrasRoot.querySelectorAll(config.countSelector),
    summaryAction: extrasRoot.querySelector(config.openSelector),
    summaryCopy: extrasRoot.querySelector(config.openSelector)?.closest('.pdp-extras__item')?.querySelector('.pdp-extras__item-copy'),
    summaryMedia: extrasRoot.querySelector(config.openSelector)?.closest('.pdp-extras__item')?.querySelector('.pdp-extras__item-media'),
    loaded: false,
  }));

  // Variant frames map: { variantId: [handle, ...] }
  let variantFramesMap = {};
  const variantFramesScript = document.querySelector('[data-variant-frames]');
  if (variantFramesScript) {
    try {
      variantFramesMap = JSON.parse(variantFramesScript.textContent);
    } catch (_) {}
  }

  const getSelectedVariantId = () => {
    const scriptEl = variantSelects.querySelector('[data-selected-variant]');
    try {
      return JSON.parse(scriptEl?.textContent)?.id?.toString() || null;
    } catch (_) {
      return null;
    }
  };

  let currentVariantId = getSelectedVariantId();

  const setCount = (config, text) => {
    config.countEls.forEach((el) => {
      el.textContent = text;
    });
  };

  const normaliseMoneyValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;
    return numericValue > 999 ? numericValue : Math.round(numericValue * 100);
  };

  const formatMoney = (valueInPence) => {
    try {
      return Shopify.formatMoney(valueInPence, window.theme?.money_format || '£{{amount}}');
    } catch (e) {
      return `£${(valueInPence / 100).toFixed(2)}`;
    }
  };

  const hasFreeHandlePackMetafield = (product) => {
    const sashProductValue = product?.metafields?.custom?.sash_product;

    if (typeof sashProductValue === 'string') {
      return sashProductValue.trim().toLowerCase() === 'free handle pack';
    }

    if (sashProductValue && typeof sashProductValue === 'object') {
      const value = sashProductValue.value;
      if (typeof value === 'string') {
        return value.trim().toLowerCase() === 'free handle pack';
      }
    }

    return false;
  };

  const FREE_HANDLE_PACK_HANDLES = [
    'deanta-fortuna-latch-kit-satin-finish',
    'deanta-augustus-latch-kit-matt-polished-chrome-finish',
    'deanta-veritas-latch-kit-polished-chrome-finish',
  ];

  const isFreeHandlePackProduct = (product) => {
    if (hasFreeHandlePackMetafield(product)) return true;

    const productHandle = String(product?.handle || '')
      .trim()
      .toLowerCase();

    return FREE_HANDLE_PACK_HANDLES.includes(productHandle);
  };

  const renderProducts = (config, products) => {
    if (!config.list) return;
    config.list.innerHTML = '';
  
    const truncateTitle = (text, maxLength = 50) => {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return `${text.slice(0, maxLength).trimEnd()}…`;
    };
  
    products.forEach((product) => {
      const firstVariant = product?.variants?.find((v) => v.available) || product?.variants?.[0];
      if (!firstVariant) return;
  
      const card = document.createElement('article');
      card.className = 'pdp-extras-product';
  
      // /products/<handle>.js returns images as URL strings and featured_image as a URL string.
      // Keep a fallback for older .json-shaped data just in case.
      const imgSrc =
        product.featured_image ||
        (typeof product.images?.[0] === 'string' ? product.images[0] : product.images?.[0]?.src) ||
        '';
  
      const isFreeHandlePack = isFreeHandlePackProduct(product);
      const originalPrice = normaliseMoneyValue(firstVariant.price || firstVariant.compare_at_price);
      const displayPrice = isFreeHandlePack ? 0 : originalPrice;
      const fullTitle = product.title || '';
      const displayTitle = truncateTitle(fullTitle, 50);
      const freeBadgeMarkup = isFreeHandlePack ? '<span class="pdp-extras-product__badge">FREE</span>' : '';
      const comparePriceMarkup =
        isFreeHandlePack && originalPrice > 0
          ? `<span class="pdp-extras-product__price-was">${formatMoney(originalPrice)}</span>`
          : '';
  
      const isDoorFrame = config.key === 'doorFrame';
      const addButtonMarkup = isDoorFrame
        ? `<button type="button" class="pdp-extras-product__add" data-choose-size>CHOOSE FRAME DEPTH</button>`
        : `<button type="button" class="pdp-extras-product__add" data-variant-id="${firstVariant.id}">ADD TO ORDER</button>`;
  
      card.innerHTML = `
        <div class="pdp-extras-product__image-wrap">
          ${freeBadgeMarkup}
          ${imgSrc ? `<img class="pdp-extras-product__image" src="${imgSrc}" alt="${product.title}" loading="lazy">` : ''}
        </div>
        <p class="pdp-extras-product__title" title="${fullTitle}">${displayTitle}</p>
        <p class="pdp-extras-product__price">
          <span class="pdp-extras-product__price-now" data-price-pence="${displayPrice}">${formatMoney(displayPrice)}</span>
          ${comparePriceMarkup}
        </p>
        ${addButtonMarkup}
      `;
  
      if (isDoorFrame) {
        card.dataset.productData = JSON.stringify({
          title: fullTitle,
          vendor: product.vendor || '',
          image: imgSrc,
          variants: (product.variants || []).map((v) => ({
            id: v.id,
            title: v.title,
            price: normaliseMoneyValue(v.price),
            // Fail-closed: only trust a real boolean. Anything else -> treat as sold out.
            available: typeof v.available === 'boolean' ? v.available : false,
          })),
        });
      }
  
      config.list.appendChild(card);
    });
  };

  const fetchCollectionProducts = async (handle) => {
    const response = await fetch(`/collections/${handle}/products.json?limit=12`, {
      headers: { Accept: 'application/json' },
    });

    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('application/json')) return null;

    const data = await response.json();
    return data?.products || [];
  };

  const fetchProductsByHandles = async (handles) => {
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return null;
  
          // NOTE: /products/<handle>.js returns Content-Type: text/javascript
          // (not application/json), but the body IS valid JSON. Accept either.
          const contentType = response.headers.get('content-type') || '';
          const looksJson =
            contentType.includes('application/json') ||
            contentType.includes('text/javascript') ||
            contentType.includes('application/javascript');
          if (!looksJson) return null;
  
          // .js endpoint returns the product object directly (no `.product` wrapper).
          // Keep a fallback in case a future response adds one.
          const data = await response.json();
          return data?.product || data || null;
        } catch (_) {
          return null;
        }
      })
    );
    return results.filter(Boolean);
  };

  const loadProducts = async (config) => {
    // Door frames are loaded per-variant from the variant metafield
    if (config.useVariantFrames) {
      const variantId = currentVariantId;
      if (config.loadedVariantId === variantId) return;

      try {
        setCount(config, 'Loading options...');
        const handles = variantId ? (variantFramesMap[variantId] || []) : [];
        extrasDebug('load:start', { key: config.key, source: 'variant-metafield', variantId, handles });

        const products = handles.length ? await fetchProductsByHandles(handles) : [];

        renderProducts(config, products);
        setDefaultMediaImage(config, products);
        setCount(config, `${products.length} Options`);
        config.loadedVariantId = variantId;
        const itemEl = config.summaryAction?.closest('.pdp-extras__item');
        if (itemEl) itemEl.hidden = products.length === 0;
        extrasDebug('load:success', { key: config.key, count: products.length });
      } catch (error) {
        setCount(config, '0 Options');
        if (config.list) {
          config.list.innerHTML = `<p class="pdp-extras-modal__empty">Unable to load ${config.title} right now.</p>`;
        }
        const itemEl = config.summaryAction?.closest('.pdp-extras__item');
        if (itemEl) itemEl.hidden = true;
        extrasDebug('load:error', { key: config.key, message: error?.message || String(error) });
      }
      return;
    }

    if (config.loaded) return;

    try {
      setCount(config, 'Loading options...');
      let products = [];

      // For configs with a productsAttr, use metafield products only — no collection fallback
      if (config.productsAttr) {
        const productHandlesRaw = extrasRoot.getAttribute(config.productsAttr);
        if (!productHandlesRaw) throw new Error(`No ${config.title} found`);
        const handles = productHandlesRaw.split(',').map((h) => h.trim()).filter(Boolean);
        if (!handles.length) throw new Error(`No ${config.title} found`);
        extrasDebug('load:start', { key: config.key, source: 'metafield', handles });
        products = await fetchProductsByHandles(handles);
      }

      // Fall back to collection only for configs without a productsAttr
      if (!products.length && !config.productsAttr) {
        const collectionHandlesRaw = extrasRoot.getAttribute(config.collectionAttr);
        if (!collectionHandlesRaw) throw new Error(`No ${config.title} found`);

        const collectionHandles = collectionHandlesRaw.split(',').map((h) => h.trim()).filter(Boolean);
        extrasDebug('load:start', { key: config.key, source: 'collection', collectionHandles });

        for (const handle of collectionHandles) {
          const result = await fetchCollectionProducts(handle);
          if (Array.isArray(result) && result.length) {
            products = result;
            break;
          }
        }
      }

      if (!products.length) {
        throw new Error(`No ${config.title} found`);
      }

      renderProducts(config, products);
      setDefaultMediaImage(config, products);
      setCount(config, `${products.length} Options`);
      config.loaded = true;
      const itemEl = config.summaryAction?.closest('.pdp-extras__item');
      if (itemEl) itemEl.hidden = products.length === 0;
      extrasDebug('load:success', { key: config.key, count: products.length });
    } catch (error) {
      setCount(config, '0 Options');
      if (config.list) {
        config.list.innerHTML = `<p class="pdp-extras-modal__empty">Unable to load ${config.title} right now.</p>`;
      }
      const itemEl = config.summaryAction?.closest('.pdp-extras__item');
      if (itemEl) itemEl.hidden = true;
      extrasDebug('load:error', { key: config.key, message: error?.message || String(error) });
    }
  };

  const updateModalSelectionState = (config) => {
    if (!config.list) return;
    const storedPayload = extrasRoot.dataset[config.key];

    // Reset all cards to their default button state first
    config.list.querySelectorAll('.pdp-extras-product').forEach((card) => {
      const btn = card.querySelector('.pdp-extras-product__add');
      if (!btn) return;
      if (btn.dataset.remove) {
        const isDoorFrame = config.key === 'doorFrame';
        btn.textContent = isDoorFrame ? 'CHOOSE FRAME DEPTH' : 'ADD TO ORDER';
        delete btn.dataset.remove;
        btn.classList.remove('pdp-extras-product__add--remove');
      }
    });

    if (!storedPayload) return;

    let selectedVariantId;
    try {
      selectedVariantId = String(JSON.parse(storedPayload).variantId);
    } catch (_) {
      return;
    }

    config.list.querySelectorAll('.pdp-extras-product').forEach((card) => {
      const btn = card.querySelector('.pdp-extras-product__add');
      if (!btn) return;

      let isSelected = false;
      if (config.key === 'doorFrame') {
        try {
          const productData = JSON.parse(card.dataset.productData || '{}');
          isSelected = (productData.variants || []).some((v) => String(v.id) === selectedVariantId);
        } catch (_) {}
      } else {
        isSelected = btn.getAttribute('data-variant-id') === selectedVariantId;
      }

      if (isSelected) {
        btn.textContent = 'REMOVE';
        btn.dataset.remove = 'true';
        btn.classList.add('pdp-extras-product__add--remove');
      }
    });
  };

  const showModal = async (config) => {
    extrasDebug('modal:open', { key: config.key });
    await loadProducts(config);
    if (!config.modal) return;
    updateModalSelectionState(config);
    config.modal.hidden = false;
    document.body.classList.add('overflow-hidden');
  };

  const hideModal = (config) => {
    extrasDebug('modal:close', { key: config.key });
    if (!config.modal) return;
    config.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
  };

  const restoreSelections = () => {
    extrasConfigs.forEach((config) => {
      const storedPayload = extrasRoot.dataset[config.key];
      if (!storedPayload) return;

      try {
        JSON.parse(storedPayload);
        setSummaryAddedState(config);
      } catch (error) {
        delete extrasRoot.dataset[config.key];
      }
    });

    if (extrasSelectionInput) {
      extrasSelectionInput.value = [
        extrasRoot.dataset.handlePack || '',
        extrasRoot.dataset.doorFrame || '',
      ]
        .filter(Boolean)
        .join('||');
    }

    if (extrasPayloadInput) {
      extrasPayloadInput.value = JSON.stringify({
        handlePack: extrasRoot.dataset.handlePack ? JSON.parse(extrasRoot.dataset.handlePack) : null,
        doorFrame: extrasRoot.dataset.doorFrame ? JSON.parse(extrasRoot.dataset.doorFrame) : null,
      });
    }

    extrasDebug('state:restored', {
      extrasSelection: extrasSelectionInput?.value || '',
      extrasPayload: extrasPayloadInput?.value || '',
    });

    updatePriceDisplay();
  };

  const showExtras = async () => {
    extrasDebug('extras:show:start', { hidden: extrasRoot.hidden, persistedState });
    persistedState.isVisible = false;
    await Promise.all(extrasConfigs.map((config) => loadProducts(config)));

    const hasVisibleItems = extrasConfigs.some((config) => {
      const itemEl = config.summaryAction?.closest('.pdp-extras__item');
      return itemEl && !itemEl.hidden;
    });

    if (hasVisibleItems) {
      persistedState.isVisible = true;
      extrasRoot.hidden = false;
      extrasDebug('extras:show', { hidden: extrasRoot.hidden, persistedState });
    } else {
      extrasRoot.hidden = true;
      extrasDebug('extras:hidden:no-products', { hidden: extrasRoot.hidden });
    }
  };

  const updatePriceDisplay = () => {
    const priceGrid = document.querySelector('.price-grid-main');
    if (!priceGrid) return;

    const basePricePence = parseInt(priceGrid.dataset.basePrice, 10) || 0;
    const baseTypicalPence = parseInt(priceGrid.dataset.baseTypical, 10) || 0;

    let extrasPence = 0;
    try { if (extrasRoot.dataset.handlePack) extrasPence += JSON.parse(extrasRoot.dataset.handlePack).pricePence || 0; } catch (_) {}
    try { if (extrasRoot.dataset.doorFrame) extrasPence += JSON.parse(extrasRoot.dataset.doorFrame).pricePence || 0; } catch (_) {}

    const totalPricePence = basePricePence + extrasPence;

    // Update Our Price — keep data-original-price in sync so the VAT
    // MutationObserver doesn't overwrite this with the stale door-only value.
    const currentEl = priceGrid.querySelector('.nx-price-current');
    if (currentEl) {
      const formatted = formatMoney(totalPricePence);
      currentEl.textContent = formatted;
      currentEl.setAttribute('data-original-price', formatted);
    }

    // Recalculate saving and typical if there is a base typical price.
    if (baseTypicalPence > 0) {
      const totalTypicalPence = baseTypicalPence + extrasPence;
      const totalSavingPence = totalTypicalPence - totalPricePence;

      const typicalEl = priceGrid.querySelector('.nx-price-typical');
      if (typicalEl) {
        const formatted = formatMoney(totalTypicalPence);
        typicalEl.textContent = formatted;
        typicalEl.setAttribute('data-original-price', formatted);
      }

      const savingEl = priceGrid.querySelector('.nx-price-saving-amount');
      if (savingEl) {
        const formatted = formatMoney(Math.max(0, totalSavingPence));
        savingEl.textContent = formatted;
        savingEl.setAttribute('data-saving-amount', formatted);
        savingEl.setAttribute('data-original-price', formatted);
      }

      const savingPctEl = priceGrid.querySelector('.nx-price-saving-percent');
      if (savingPctEl && totalTypicalPence > 0) {
        const pct = Math.round(Math.max(0, totalSavingPence) * 100 / totalTypicalPence);
        savingPctEl.textContent = String(pct);
        savingPctEl.setAttribute('data-saving-percent', String(pct));
      }
    }
  };

  const setDefaultMediaImage = (config, products) => {
    if (!config.summaryMedia) return;
    if (extrasRoot.dataset[config.key]) return;
    const p = products?.[0];
    const firstImg =
      p?.featured_image ||
      (typeof p?.images?.[0] === 'string' ? p.images[0] : p?.images?.[0]?.src);
    if (firstImg) {
      config.summaryMedia.innerHTML = `<img src="${firstImg}" alt="" class="pdp-extras__item-media-img">`;
    }
  };

  const setSummaryAddedState = (config) => {
    if (!config.summaryAction) return;

    config.summaryAction.classList.add('pdp-extras__action--added');
    config.summaryAction.innerHTML = 'ADDED ✓';

    updatePriceDisplay();

    if (config.summaryMedia) {
      try {
        const stored = JSON.parse(extrasRoot.dataset[config.key] || '{}');
        if (stored.image) {
          config.summaryMedia.innerHTML = `<img src="${stored.image}" alt="" class="pdp-extras__item-media-img">`;
        }
      } catch (_) {}
    }

    if (!config.summaryCopy) return;
    if (config.summaryCopy.querySelector(`[data-${config.key}-change]`)) return;

    const changeBtn = document.createElement('button');
    changeBtn.type = 'button';
    changeBtn.className = 'pdp-extras__item-link';
    changeBtn.setAttribute(`data-${config.key}-change`, 'true');
    changeBtn.textContent = 'Change';
    changeBtn.addEventListener('click', () => showModal(config));
    config.summaryCopy.appendChild(changeBtn);
  };

  const resetSummaryState = (config) => {
    if (!config.summaryAction) return;
    config.summaryAction.classList.remove('pdp-extras__action--added');
    config.summaryAction.innerHTML = 'ADD <span class="pdp-extras__plus">+</span>';
    config.summaryCopy?.querySelector(`[data-${config.key}-change]`)?.remove();
    if (config.summaryMedia) config.summaryMedia.innerHTML = '';
    delete extrasRoot.dataset[config.key];
    if (persistedState) persistedState[config.key] = null;
    updatePriceDisplay();
  };

  const onVariantChange = (event) => {
    const target = event.target;
    if (!target) return;

    const isSizeSelect =
      target.matches('select[name^="options["]') && (target.getAttribute('name') || '').toLowerCase().includes('size');

    const isSizeRadio =
      target.matches('fieldset input[type="radio"]') &&
      (target.closest('fieldset')?.querySelector('legend')?.textContent || '').toLowerCase().includes('size');

    extrasDebug('variant:change', {
      targetName: target.getAttribute('name') || target.name || null,
      targetValue: target.value || null,
      isSizeSelect,
      isSizeRadio,
    });

    if (isSizeSelect || isSizeRadio) {
      window.__pdpSizeInteracted = true;
      if (isSizeSelect) syncSizeLabel(target);
      showExtras();
    }
  };

  variantSelects.addEventListener('change', onVariantChange);
  cleanupCallbacks.push(() => variantSelects.removeEventListener('change', onVariantChange));

  const unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    extrasConfigs.forEach((config) => resetSummaryState(config));
    if (extrasSelectionInput) extrasSelectionInput.value = '';
    if (extrasPayloadInput) extrasPayloadInput.value = '';
  });
  cleanupCallbacks.push(unsubscribeCartUpdate);

  const unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const newVariantId = event?.data?.variant?.id?.toString();
    if (!newVariantId || newVariantId === currentVariantId) return;

    currentVariantId = newVariantId;
    extrasDebug('variant:resolved', { newVariantId });

    const doorFrameConfig = extrasConfigs.find((c) => c.key === 'doorFrame');
    if (doorFrameConfig) {
      doorFrameConfig.loadedVariantId = null;
      loadProducts(doorFrameConfig);
    }
  });
  cleanupCallbacks.push(unsubscribeVariantChange);

  extrasConfigs.forEach((config) => {
    config.openButtons.forEach((btn) => {
      const onOpen = () => showModal(config);
      btn.addEventListener('click', onOpen);
      cleanupCallbacks.push(() => btn.removeEventListener('click', onOpen));
    });

    config.closeButtons.forEach((btn) => {
      const onClose = () => hideModal(config);
      btn.addEventListener('click', onClose);
      cleanupCallbacks.push(() => btn.removeEventListener('click', onClose));
    });
  });

  extrasConfigs.forEach((config) => {
    const onListClick = async (event) => {
      const addBtn = event.target.closest('.pdp-extras-product__add');
      if (!addBtn) return;

      // Handle remove
      if (addBtn.dataset.remove) {
        event.stopImmediatePropagation();
        addBtn.textContent = config.key === 'doorFrame' ? 'CHOOSE FRAME DEPTH' : 'ADD TO ORDER';
        delete addBtn.dataset.remove;
        addBtn.classList.remove('pdp-extras-product__add--remove');
        resetSummaryState(config);
        if (extrasSelectionInput) {
          extrasSelectionInput.value = [
            extrasRoot.dataset.handlePack || '',
            extrasRoot.dataset.doorFrame || '',
          ].filter(Boolean).join('||');
        }
        if (extrasPayloadInput) {
          extrasPayloadInput.value = JSON.stringify({
            handlePack: extrasRoot.dataset.handlePack ? JSON.parse(extrasRoot.dataset.handlePack) : null,
            doorFrame: extrasRoot.dataset.doorFrame ? JSON.parse(extrasRoot.dataset.doorFrame) : null,
          });
        }
        return;
      }

      const variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      const productCard = addBtn.closest('.pdp-extras-product');
      const title = productCard?.querySelector('.pdp-extras-product__title')?.textContent?.trim() || '';
      const priceEl = productCard?.querySelector('.pdp-extras-product__price-now');
      const price = priceEl?.textContent?.trim() || '';
      const pricePence = parseInt(priceEl?.dataset.pricePence || '0', 10);
      const cardImage = productCard?.querySelector('.pdp-extras-product__image')?.src || '';

      const originalText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'ADDING...';

      const payload = {
        key: config.key,
        variantId: Number(variantId),
        title,
        price,
        pricePence,
        image: cardImage,
      };

      extrasRoot.dataset[config.key] = JSON.stringify(payload);
      persistedState[config.key] = extrasRoot.dataset[config.key];
      if (extrasSelectionInput) {
        extrasSelectionInput.value = [
          extrasRoot.dataset.handlePack || '',
          extrasRoot.dataset.doorFrame || '',
        ]
          .filter(Boolean)
          .join('||');
      }
      if (extrasPayloadInput) {
        extrasPayloadInput.value = JSON.stringify({
          handlePack: extrasRoot.dataset.handlePack ? JSON.parse(extrasRoot.dataset.handlePack) : null,
          doorFrame: extrasRoot.dataset.doorFrame ? JSON.parse(extrasRoot.dataset.doorFrame) : null,
        });
      }

      extrasDebug('selection:added', {
        key: config.key,
        payload,
        extrasSelection: extrasSelectionInput?.value || '',
        extrasPayload: extrasPayloadInput?.value || '',
      });

      addBtn.textContent = 'ADDED';
      setSummaryAddedState(config);
      hideModal(config);

      setTimeout(() => {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      }, 600);
    };

    config.list?.addEventListener('click', onListClick);
    if (config.list) {
      cleanupCallbacks.push(() => config.list.removeEventListener('click', onListClick));
    }
  });

  // Door frame size picker sub-modal
  const doorFrameConfig = extrasConfigs.find((c) => c.key === 'doorFrame');
  const sizeModal = extrasRoot.querySelector('[data-door-frame-size-modal]');
  const sizeContent = sizeModal?.querySelector('[data-door-frame-size-content]');

  const closeSizeModal = () => {
    if (sizeModal) sizeModal.hidden = true;
  };

  const openSizeModal = (productData) => {
    if (!sizeModal || !sizeContent || !doorFrameConfig) return;
    const { title, vendor, image, variants } = productData;
    console.log(variants, 'data variants');
  
    // Prefer the first AVAILABLE variant; fall back to variants[0] only if every one is sold out.
    const firstAvailable = variants.find((v) => v.available) || variants[0];
    const initialPrice     = firstAvailable ? firstAvailable.price : 0;
    const initialAvailable = !!firstAvailable?.available;
    const initialVariantId = firstAvailable?.id || '';
  
    const optionsHtml = variants
      .map((v) => {
        const isSelected = v.id === initialVariantId ? 'selected' : '';
        const disabled   = v.available ? '' : 'disabled';
        const label      = v.available ? v.title : `${v.title} - Sold out`;
        return `<option
                  value="${v.id}"
                  data-price="${v.price}"
                  data-available="${!!v.available}"
                  ${disabled} ${isSelected}>${label}</option>`;
      })
      .join('');
  
    sizeContent.innerHTML = `
      <div class="pdp-extras-size-card">
        <div class="pdp-extras-size-card__media">
          ${image ? `<img src="${image}" alt="${title}" class="pdp-extras-size-card__image">` : ''}
        </div>
        <div class="pdp-extras-size-card__heading">
          ${vendor ? `<p class="pdp-extras-size-card__vendor">${vendor}</p>` : ''}
          <h4 class="pdp-extras-size-card__title">${title}</h4>
        </div>
        <div class="pdp-extras-size-card__info">
          <p class="pdp-extras-size-card__label"><strong>Choose your size</strong></p>
          <select class="pdp-extras-size-card__select" data-size-select>${optionsHtml}</select>
          <p class="pdp-extras-size-card__stock ${initialAvailable ? 'in-stock' : 'out-of-stock'}" data-size-stock>
            ${initialAvailable ? '✓ In Stock' : 'Out of Stock'}
          </p>
          <button type="button"
                  class="pdp-extras-size-card__add"
                  data-size-add
                  data-variant-id="${initialVariantId}"
                  ${initialAvailable ? '' : 'disabled'}>
            ${initialAvailable ? `${formatMoney(initialPrice)} INC. VAT – ADD TO ORDER` : 'Sold out'}
          </button>
        </div>
      </div>
    `;
  
    const selectEl = sizeContent.querySelector('[data-size-select]');
    const stockEl  = sizeContent.querySelector('[data-size-stock]');
    const addBtn   = sizeContent.querySelector('[data-size-add]');
  
    // Ensure the initially-selected option matches the first available variant.
    if (firstAvailable) selectEl.value = String(firstAvailable.id);
  
    const syncFromSelect = () => {
      const opt = selectEl.options[selectEl.selectedIndex];
      if (!opt) return;
      const price     = parseFloat(opt.dataset.price) || 0;
      const available = opt.dataset.available === 'true';
  
      stockEl.textContent = available ? '✓ In Stock' : 'Out of Stock';
      stockEl.className   = `pdp-extras-size-card__stock ${available ? 'in-stock' : 'out-of-stock'}`;
  
      addBtn.dataset.variantId = opt.value;
      addBtn.textContent = available
        ? `${formatMoney(price)} INC. VAT – ADD TO ORDER`
        : 'Sold out';
      addBtn.disabled = !available;
    };
  
    selectEl?.addEventListener('change', syncFromSelect);
  
    addBtn?.addEventListener('click', () => {
      if (addBtn.disabled) return;
      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;
  
      const opt = selectEl?.options[selectEl?.selectedIndex];
      if (!opt || opt.dataset.available !== 'true') return;
  
      const pricePence = parseFloat(opt.dataset.price || '0') || 0;
      const price = formatMoney(pricePence);
      const cleanOptText = (opt.text || '').replace(/ - Sold out$/, '');
      const sizeTitle = cleanOptText ? `${title} - ${cleanOptText}` : title;
  
      const payload = {
        key: doorFrameConfig.key,
        variantId: Number(variantId),
        title: sizeTitle,
        price,
        pricePence,
        image,
      };
  
      extrasRoot.dataset[doorFrameConfig.key] = JSON.stringify(payload);
      persistedState[doorFrameConfig.key] = extrasRoot.dataset[doorFrameConfig.key];
  
      if (extrasSelectionInput) {
        extrasSelectionInput.value = [
          extrasRoot.dataset.handlePack || '',
          extrasRoot.dataset.doorFrame || '',
        ].filter(Boolean).join('||');
      }
      if (extrasPayloadInput) {
        extrasPayloadInput.value = JSON.stringify({
          handlePack: extrasRoot.dataset.handlePack ? JSON.parse(extrasRoot.dataset.handlePack) : null,
          doorFrame:  extrasRoot.dataset.doorFrame  ? JSON.parse(extrasRoot.dataset.doorFrame)  : null,
        });
      }
  
      extrasDebug('selection:added', { key: doorFrameConfig.key, payload });
      setSummaryAddedState(doorFrameConfig);
      closeSizeModal();
      hideModal(doorFrameConfig);
    });
  
    sizeModal.hidden = false;
  };

  const onSizeBack = () => closeSizeModal();
  const onSizeClose = () => { closeSizeModal(); if (doorFrameConfig) hideModal(doorFrameConfig); };

  sizeModal?.querySelectorAll('[data-door-frame-size-back]').forEach((btn) => {
    btn.addEventListener('click', onSizeBack);
    cleanupCallbacks.push(() => btn.removeEventListener('click', onSizeBack));
  });
  sizeModal?.querySelectorAll('[data-door-frame-size-close]').forEach((btn) => {
    btn.addEventListener('click', onSizeClose);
    cleanupCallbacks.push(() => btn.removeEventListener('click', onSizeClose));
  });

  if (doorFrameConfig?.list) {
    const onChooseSize = (event) => {
      const chooseBtn = event.target.closest('[data-choose-size]');
      if (!chooseBtn) return;
      if (chooseBtn.dataset.remove) return;
      const card = chooseBtn.closest('.pdp-extras-product');
      if (!card?.dataset.productData) return;
      try {
        openSizeModal(JSON.parse(card.dataset.productData));
      } catch (_) {}
    };
    doorFrameConfig.list.addEventListener('click', onChooseSize);
    cleanupCallbacks.push(() => doorFrameConfig.list.removeEventListener('click', onChooseSize));
  }

  restoreSelections();

  if (persistedState.isVisible || extrasRoot.dataset.handlePack || extrasRoot.dataset.doorFrame) {
    extrasDebug('init:show-on-load', {
      isVisible: persistedState.isVisible,
      hasHandlePack: Boolean(extrasRoot.dataset.handlePack),
      hasDoorFrame: Boolean(extrasRoot.dataset.doorFrame),
    });
    showExtras();
  }

  window.__pdpExtrasPersistedState = persistedState;
  extrasDebug('init:complete', {
    hidden: extrasRoot.hidden,
    persistedState: window.__pdpExtrasPersistedState,
  });

  window.__pdpExtrasState = {
    root: extrasRoot,
    cleanup() {
      cleanupCallbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error(error);
        }
      });
    },
  };

}

document.addEventListener('DOMContentLoaded', initProductExtras);
document.addEventListener('product-info:loaded', () => {
  setTimeout(initProductExtras, 50);
});
// Re-init after each AJAX variant render so syncSizeLabel restores the size label on the new variant-selects HTML
subscribe(PUB_SUB_EVENTS.variantChange, () => {
  setTimeout(initProductExtras, 0);
});
document.addEventListener('product-info:loaded', () => {
  setTimeout(moveProductExtrasIntoSelectorPanel, 0);
  setTimeout(syncProductExtrasVisibility, 250);
  setTimeout(syncProductExtrasVisibility, 600);
});
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(moveProductExtrasIntoSelectorPanel, 0);
});


   document.addEventListener("DOMContentLoaded", function () {

    /* ----------------- Generic redirect for Quote button ----------------- */
    function bindRedirectClick(sourceSelector, targetSelector) {
      const sourceBtn = document.querySelector(sourceSelector);
      if (!sourceBtn) return;

      sourceBtn.addEventListener('click', function (e) {
        e.preventDefault();
        let observer;

        function tryTrigger() {
          const targetBtn = document.querySelector(targetSelector);
          if (targetBtn) {
            targetBtn.click();
            if (observer) observer.disconnect();
          }
        }

        observer = new MutationObserver(tryTrigger);
        observer.observe(document.body, { childList: true, subtree: true });

        tryTrigger();
      });
    }

    /* Quote → RFQ button */
    bindRedirectClick('.quote_button', '.omgrfq-btn-quotes');

    /* ---------------- Wishlist custom logic ---------------- */
    const wishlistBtn = document.querySelector('.wishlist_button');
    const btnText = document.querySelector('.wishlist_button span.nx-btn-text');

    if (wishlistBtn && btnText) {
      let bodyObserver;
      let appBtnObserver;
      let currentAppBtn = null;

      const getAppBtn = () => document.querySelector('.wk-button');

      function updateText() {
        btnText.textContent = wishlistBtn.classList.contains('wk-selected') ? 'In wishlist' : 'Add to wishlist';
      }

      function isWishlisted(appBtn) {
        if (!appBtn) return false;

        // Different wishlist apps / versions represent state differently.
        // Prefer explicit state first, then fall back to label/text heuristics.
        if (appBtn.classList.contains('wk-selected')) return true;

        const ariaPressed = appBtn.getAttribute('aria-pressed');
        if (ariaPressed === 'true') return true;

        const ariaLabel = (appBtn.getAttribute('aria-label') || '').toLowerCase();
        if (ariaLabel.includes('remove') || ariaLabel.includes('in wishlist') || ariaLabel.includes('wishlisted')) return true;

        const text = (appBtn.querySelector('.wk-text')?.textContent || appBtn.textContent || '').toLowerCase();
        if (text.includes('in wishlist') || text.includes('wishlisted') || text.includes('remove')) return true;

        return false;
      }

      function syncStateFrom(appBtn) {
        if (!appBtn) return;

        wishlistBtn.classList.toggle('wk-selected', isWishlisted(appBtn));
        updateText();
      }

      function bindToAppBtn(appBtn) {
        if (!appBtn) return;

        // Keep our custom button state in sync with the app button state.
        syncStateFrom(appBtn);

        // The app can add/remove `wk-selected` AFTER the button exists.
        // Observe class changes so we don't miss "already in wishlist" state.
        appBtnObserver?.disconnect();
        appBtnObserver = new MutationObserver(() => syncStateFrom(currentAppBtn || appBtn));
        appBtnObserver.observe(appBtn, {
          attributes: true,
          attributeFilter: ['class', 'aria-label', 'aria-pressed'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      function maybeBindAppBtn() {
        // If the app button was replaced/removed, allow rebind.
        if (currentAppBtn && !document.contains(currentAppBtn)) currentAppBtn = null;

        const appBtn = getAppBtn();
        if (!appBtn) return;

        // Avoid rebinding on every DOM mutation (some pages mutate constantly).
        if (appBtn === currentAppBtn) return;
        currentAppBtn = appBtn;
        bindToAppBtn(appBtn);
      }

      // Observe the document for the app's button being inserted/replaced.
      bodyObserver = new MutationObserver(maybeBindAppBtn);
      bodyObserver.observe(document.body, { childList: true, subtree: true });

      // Initial attempt (in case the app button is already on the page)
      maybeBindAppBtn();

      wishlistBtn.addEventListener('click', function (e) {
        e.preventDefault();

        const appBtn = getAppBtn();
        if (!appBtn) return;

        appBtn.click();

        // Fallback sync in case the app updates state asynchronously.
        setTimeout(() => syncStateFrom(getAppBtn()), 300);
      });
    }

    
    const buttonMap = {
      ask_a_question_button: '.ask_a_question .floating-button',
      request_callback_button: '.request_callback .floating-button',
      price_match_button: '.price_match .floating-button'
    };

    document.querySelectorAll('.nx-info-link').forEach(link => {
      link.addEventListener('click', function(e) {
        e.preventDefault();

        for (const key in buttonMap) {
          if (link.classList.contains(key)) {
            const targetBtn = document.querySelector(buttonMap[key]);
            targetBtn?.click();
          }
        }
      });
  });

  

  });

/* -------------------------------------------------
 * Collection product-card hover image (single handler)
 * Previously this was injected once per product card, which is extremely expensive
 * on collection pages with many products.
 * ------------------------------------------------- */
(() => {
  if (window.__cstHoverImageBound) return;
  window.__cstHoverImageBound = true;

  document.addEventListener(
    'mouseover',
    (e) => {
      const box = e.target?.closest?.('.cst-product-img-box');
      if (!box) return;
      const img = box.querySelector('.js-hover-image');
      if (img && !img.getAttribute('src')) {
        const src = img.dataset?.src;
        if (src) img.setAttribute('src', src);
      }
    },
    { passive: true }
  );
})();

/* -----------------------
 * perfDebug logging (opt-in)
 * Usage: add ?perfDebug=1 to any URL
 * ----------------------- */
(() => {
  const perfDebug = new URLSearchParams(window.location.search).get('perfDebug') === '1';
  if (!perfDebug) return;

  window.__perfDebugData = window.__perfDebugData || {};

  // Puppeteer / sandboxed environments can print objects as "JSHandle@object".
  // Prefer JSON strings so the data is readable from remote console capture.
  // eslint-disable-next-line no-console
  const log = (label, data) => {
    try {
      const payload = typeof data === 'string' ? data : JSON.stringify(data);
      // Log as a single string so remote console capture doesn't show JSHandle@object
      // eslint-disable-next-line no-console
      console.log(`[perfDebug] ${label} ${payload}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(`[perfDebug] ${label} ${String(data)}`);
    }
  };

  // Print any errors captured before deferred scripts ran (see `layout/theme.liquid`).
  try {
    if (Array.isArray(window.__earlyErrors) && window.__earlyErrors.length) {
      log('earlyErrors', window.__earlyErrors.slice(0, 20));
    }
  } catch (e) {
    // ignore
  }

  // Capture runtime errors/rejections (often the cause of repeated retries + LongTasks).
  // This helps identify where `share-modal.js` is coming from when it is injected as inline code.
  try {
    window.addEventListener(
      'error',
      (event) => {
        const payload = {
          message: event?.message || null,
          filename: event?.filename || null,
          lineno: event?.lineno || null,
          colno: event?.colno || null,
          stack: event?.error?.stack || null,
        };
        log('window.error', payload);
      },
      true
    );
  } catch (e) {
    // ignore
  }

  try {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event?.reason;
      const payload = {
        message: reason?.message || (typeof reason === 'string' ? reason : null),
        stack: reason?.stack || null,
      };
      log('window.unhandledrejection', payload);
    });
  } catch (e) {
    // ignore
  }

  try {
    const nav = performance.getEntriesByType('navigation')?.[0];
    if (nav) {
      const payload = {
        type: nav.type,
        ttfb_ms: +(nav.responseStart - nav.requestStart).toFixed(1),
        response_ms: +(nav.responseEnd - nav.responseStart).toFixed(1),
        domContentLoaded_ms: +nav.domContentLoadedEventEnd.toFixed(1),
        load_ms: +nav.loadEventEnd.toFixed(1),
        transferSize: nav.transferSize,
        encodedBodySize: nav.encodedBodySize,
        decodedBodySize: nav.decodedBodySize,
      };
      window.__perfDebugData.navigation = payload;
      log('navigation', payload);
    }
  } catch (e) {
    // ignore
  }

  // Log LCP + long tasks (Chrome/Edge)
  try {
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const last = entryList.getEntries().slice(-1)[0];
        if (!last) return;
        const payload = {
          startTime_ms: +last.startTime.toFixed(1),
          renderTime_ms: last.renderTime ? +last.renderTime.toFixed(1) : null,
          loadTime_ms: last.loadTime ? +last.loadTime.toFixed(1) : null,
          element: last.element ? last.element.tagName : null,
          url: last.url || null,
        };
        window.__perfDebugData.LCP = payload;
        log('LCP', payload);
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      const longTaskObserver = new PerformanceObserver((entryList) => {
        entryList.getEntries().forEach((e) => {
          if (e.duration < 100) return;
          // Chromium may provide attribution for long tasks (script/container).
          // This is the fastest way to identify the culprit without a full Performance profile.
          const attribution = Array.isArray(e.attribution)
            ? e.attribution.map((a) => ({
                name: a?.name || null,
                entryType: a?.entryType || null,
                startTime_ms: a?.startTime != null ? +a.startTime.toFixed(1) : null,
                duration_ms: a?.duration != null ? +a.duration.toFixed(1) : null,
                containerType: a?.containerType || null,
                containerSrc: a?.containerSrc || null,
                containerId: a?.containerId || null,
              }))
            : null;

          log('LongTask', {
            startTime_ms: +e.startTime.toFixed(1),
            duration_ms: +e.duration.toFixed(1),
            attribution,
          });
        });
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
    }
  } catch (e) {
    // ignore
  }

  // After window load, print largest resources + image summary
  window.addEventListener('load', () => {
    try {
      // Collection page diagnostics (helps confirm theme-level main-thread bloat)
      try {
        const cstCards = document.querySelectorAll('.cst-product-card');
        if (cstCards.length) {
          const cardInlineScripts = document.querySelectorAll('.cst-product-card script').length;
          const cardInlineStyles = document.querySelectorAll('.cst-product-card style').length;
          const grid = document.querySelector('#product-grid');

          const cstImgsInGrid = grid ? Array.from(grid.querySelectorAll('img.cst-img')) : [];
          const cstEagerImgsInGrid = cstImgsInGrid.filter((img) => img.getAttribute('loading') === 'eager').length;
          const cstLazyImgsInGrid = cstImgsInGrid.filter((img) => img.getAttribute('loading') === 'lazy').length;
          const cstUnsetImgsInGrid = cstImgsInGrid.length - cstEagerImgsInGrid - cstLazyImgsInGrid;

          log('cstCollection', {
            cstProductCards: cstCards.length,
            cardInlineScripts,
            cardInlineStyles,
            cstImgsInGrid: cstImgsInGrid.length,
            cstEagerImgsInGrid,
            cstLazyImgsInGrid,
            cstUnsetImgsInGrid,
          });
        }
      } catch (e) {
        // ignore
      }

      // Helpful when chasing injected scripts (apps/preview/third-party).
      // Surfaces unexpected scripts like `share-modal.js` without needing Network tab.
      try {
        const scriptSrcs = Array.from(document.scripts || [])
          .map((s) => s && s.src)
          .filter(Boolean);
        const suspects = scriptSrcs.filter((src) => /share-modal|share|modal/i.test(src)).slice(0, 50);
        if (suspects.length) {
          window.__perfDebugData.scriptSuspects = suspects;
          log('scriptSuspects', suspects);
        }
      } catch (e) {
        // ignore
      }

      const images = Array.from(document.images || []);
      const lazy = images.filter((img) => img.getAttribute('loading') === 'lazy').length;
      const eager = images.filter((img) => img.getAttribute('loading') === 'eager').length;
      const unset = images.length - lazy - eager;
      const imagesPayload = { total: images.length, lazy, eager, unset };
      window.__perfDebugData.images = imagesPayload;
      log('images', imagesPayload);

      const resources = performance.getEntriesByType('resource') || [];
      const top = resources
        .slice()
        .sort((a, b) => (b.duration || 0) - (a.duration || 0))
        .slice(0, 20)
        .map((r) => ({
          initiatorType: r.initiatorType,
          name: r.name,
          duration_ms: +r.duration.toFixed(1),
          transferSize: r.transferSize,
          encodedBodySize: r.encodedBodySize,
        }));
      window.__perfDebugData.topResourcesByDuration = top;
      log('topResourcesByDuration', top);
    } catch (e) {
      // ignore
    }
  });
  
})();
